import { getMutationIdempotencyPolicyForPath } from '../shared/mutation-idempotency';

export interface FetchRetryOptions {
  timeoutMs?: number;
  timeoutLabel?: string;
  networkRetryCount?: number;
  networkRetryBackoffMs?: number;
}

export interface ApiRequestOptions {
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

export type RefreshAuthToken = () => Promise<string | null>;

const DEFAULT_NETWORK_RETRY_BACKOFF_MS = 150;

function getRequestMethod(init: RequestInit): string {
  return (init.method ?? 'GET').toUpperCase();
}

function isSafeRetryMethod(method: string): boolean {
  return method === 'GET' || method === 'HEAD' || method === 'OPTIONS';
}

function isRetryableNetworkError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  if (err.name === 'AbortError') return false;

  if (err instanceof TypeError) {
    return true;
  }

  const message = err.message.toLowerCase();
  return (
    message.includes('fetch failed')
    || message.includes('failed to fetch')
    || message.includes('network error')
    || message.includes('networkerror')
    || message.includes('econnrefused')
    || message.includes('etimedout')
    || message.includes('err_connection')
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function canRetryMutationAfterUnauthorized(url: string, method: string, headers: Headers): boolean {
  const policy = getMutationIdempotencyPolicyForPath(method, url);
  if (!policy) return false;
  if (policy.classification !== 'retry-safe-with-idempotency-key') return false;
  return headers.has('Idempotency-Key');
}

function canRetryAfterUnauthorized(url: string, init: RequestInit): boolean {
  const method = getRequestMethod(init);
  if (isSafeRetryMethod(method)) {
    return true;
  }

  const headers = new Headers(init.headers);
  return canRetryMutationAfterUnauthorized(url, method, headers);
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getErrorMessageFromUnknown(payload: unknown): string | null {
  if (!isObject(payload) || !('error' in payload)) return null;
  const errorValue = payload.error;
  if (typeof errorValue === 'string') return errorValue;
  if (!isObject(errorValue)) return null;

  const message = typeof errorValue.message === 'string' ? errorValue.message : null;
  const code = typeof errorValue.code === 'string' ? errorValue.code : null;
  const details = errorValue.details;
  const issues = isObject(details) && Array.isArray(details.issues) ? details.issues : [];

  const parts: string[] = [];
  if (code) parts.push(code);
  if (message) parts.push(message);
  if (issues.length > 0) {
    const issueLines = issues
      .filter((i): i is Record<string, unknown> => isObject(i))
      .map((i) => {
        const path = Array.isArray(i.path) ? i.path.join('.') : typeof i.path === 'string' ? i.path : '';
        const msg = typeof i.message === 'string' ? i.message : '';
        return path ? `${path}: ${msg}` : msg;
      })
      .filter(Boolean);
    if (issueLines.length > 0) parts.push(issueLines.join('; '));
  }

  return parts.length > 0 ? parts.join(' — ') : null;
}

function parseJsonText(text: string): unknown {
  return JSON.parse(text);
}

export async function parseErrorMessage(response: Response): Promise<string> {
  let errorMsg = `HTTP ${response.status}`;

  try {
    const text = await response.text();
    if (!text) return errorMsg;

    try {
      const errorData = parseJsonText(text);
      const parsed = getErrorMessageFromUnknown(errorData);
      errorMsg = parsed ?? errorMsg;
    } catch {
      // Non-JSON error body; use the text to improve debuggability.
      errorMsg = text;
    }
  } catch {
    // ignore
  }

  return errorMsg;
}

/**
 * Parse a JSON response body if present.
 *
 * - Returns `{}` for empty bodies (e.g. future 204/empty-200 endpoints)
 * - Throws if the JSON body contains an `error` field
 */
export async function parseJsonEnvelope(response: Response): Promise<Record<string, unknown>> {
  // 204 should never have a body, but be defensive for 200/201 with empty body too.
  if (response.status === 204) return {};

  const text = await response.text();
  if (!text || text.trim().length === 0) return {};

  const json = parseJsonText(text);
  if (!isObject(json)) {
    throw new Error('Invalid JSON response envelope');
  }

  const errorMessage = getErrorMessageFromUnknown(json);
  if (errorMessage) {
    throw new Error(errorMessage);
  }

  return json;
}

function getEnvelopeData<T>(json: Record<string, unknown>): T {
  if (!('data' in json)) {
    throw new Error('Invalid API response: missing data envelope');
  }
  return json.data as T;
}

/**
 * Execute a fetch with timeout, optional 401 refresh+retry, and standardized error handling.
 */
export async function fetchWithRetry(
  url: string,
  init: RequestInit,
  opts: FetchRetryOptions = {},
  refreshAuthToken?: RefreshAuthToken,
): Promise<Response> {
  const { timeoutMs = 30000, timeoutLabel = 'Request' } = opts;
  const method = getRequestMethod(init);
  const networkRetriesRemaining = opts.networkRetryCount ?? (isSafeRetryMethod(method) ? 1 : 0);
  const networkRetryBackoffMs = opts.networkRetryBackoffMs ?? DEFAULT_NETWORK_RETRY_BACKOFF_MS;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  // If the caller passed an external signal (e.g. search abort), forward its
  // abort to the internal timeout controller so either can cancel the fetch.
  const externalSignal = init.signal;
  if (externalSignal) {
    if (externalSignal.aborted) {
      clearTimeout(timeoutId);
      throw new DOMException('The operation was aborted.', 'AbortError');
    }
    externalSignal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  try {
    const response = await fetch(url, { ...init, signal: controller.signal });
    clearTimeout(timeoutId);

    // Handle 401 with token refresh (one retry). Non-safe methods require an
    // explicit idempotency key before retry is allowed.
    if (response.status === 401 && refreshAuthToken && canRetryAfterUnauthorized(url, init)) {
      const newToken = await refreshAuthToken();
      if (newToken) {
        const retryHeaders = new Headers(init.headers);
        retryHeaders.set('Authorization', `Bearer ${newToken}`);

        const retryController = new AbortController();
        const retryTimeoutId = setTimeout(() => retryController.abort(), timeoutMs);
        if (externalSignal) {
          if (externalSignal.aborted) {
            clearTimeout(retryTimeoutId);
            throw new DOMException('The operation was aborted.', 'AbortError');
          }
          externalSignal.addEventListener('abort', () => retryController.abort(), { once: true });
        }
        try {
          const retryResponse = await fetch(url, {
            ...init,
            headers: retryHeaders,
            signal: retryController.signal,
          });
          clearTimeout(retryTimeoutId);
          return retryResponse;
        } catch (retryErr) {
          clearTimeout(retryTimeoutId);
          if (retryErr instanceof Error && retryErr.name === 'AbortError') {
            throw new Error(`${timeoutLabel} timed out`);
          }
          throw retryErr;
        }
      }
    }

    return response;
  } catch (err) {
    clearTimeout(timeoutId);
    if (err instanceof Error && err.name === 'AbortError') {
      // Re-throw as AbortError if the external signal triggered it,
      // so callers can distinguish user-initiated abort from timeout.
      if (externalSignal?.aborted) {
        throw err;
      }
      throw new Error(`${timeoutLabel} timed out`);
    }
    if (
      networkRetriesRemaining > 0
      && isSafeRetryMethod(method)
      && isRetryableNetworkError(err)
      && !externalSignal?.aborted
    ) {
      await sleep(networkRetryBackoffMs);
      return fetchWithRetry(
        url,
        init,
        {
          ...opts,
          networkRetryCount: networkRetriesRemaining - 1,
          networkRetryBackoffMs,
        },
        refreshAuthToken,
      );
    }
    throw err;
  }
}

export interface ApiClient {
  request<T = unknown>(
    method: string,
    path: string,
    data?: unknown,
    options?: ApiRequestOptions,
  ): Promise<T>;
  get<T = unknown>(path: string, options?: ApiRequestOptions): Promise<T>;
  post<T = unknown>(path: string, data?: unknown, options?: ApiRequestOptions): Promise<T>;
  put<T = unknown>(path: string, data?: unknown, options?: ApiRequestOptions): Promise<T>;
  delete<T = unknown>(path: string, options?: ApiRequestOptions): Promise<T>;
  upload<T = unknown>(path: string, formData: FormData, options?: ApiRequestOptions): Promise<T>;
  getHtml(path: string): Promise<string>;
  postHtml(path: string, data: unknown): Promise<string>;
  getBlob(path: string, opts?: FetchRetryOptions): Promise<Blob>;
}

export function createApiClient(opts: {
  baseUrl: string;
  getAuthHeaders: () => Record<string, string>;
  refreshAuthToken?: RefreshAuthToken;
}): ApiClient {
  const { baseUrl, getAuthHeaders, refreshAuthToken } = opts;
  const inFlightReads = new Map<string, Promise<unknown>>();

  async function request<T = unknown>(
    method: string,
    path: string,
    data: unknown = null,
    options?: ApiRequestOptions,
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    };
    for (const [key, value] of Object.entries(options?.headers ?? {})) {
      if (key.toLowerCase() === 'authorization') continue;
      headers[key] = value;
    }

    const normalizedMethod = method.toUpperCase();
    const dedupeKey = (
      normalizedMethod === 'GET'
      && !options?.signal
      && data === null
    )
      ? `${normalizedMethod}:${path}:${headers.Authorization ?? ''}`
      : null;

    if (dedupeKey) {
      const existing = inFlightReads.get(dedupeKey);
      if (existing) {
        return existing as Promise<T>;
      }
    }

    const executeRequest = async (): Promise<T> => {
      const response = await fetchWithRetry(
        `${baseUrl}${path}`,
        {
          method: normalizedMethod,
          headers,
          body: data ? JSON.stringify(data) : null,
          signal: options?.signal,
        },
        {},
        refreshAuthToken,
      );

      if (!response.ok) {
        throw new Error(await parseErrorMessage(response));
      }

      // Some endpoints may legitimately return 204.
      if (response.status === 204) {
        return undefined as T;
      }

      const json = await parseJsonEnvelope(response);
      return getEnvelopeData<T>(json);
    };

    const pendingRequest = executeRequest();
    if (dedupeKey) {
      inFlightReads.set(dedupeKey, pendingRequest as Promise<unknown>);
    }

    try {
      return await pendingRequest;
    } finally {
      if (dedupeKey) {
        inFlightReads.delete(dedupeKey);
      }
    }
  }

  return {
    request,
    get<T = unknown>(path: string, options?: ApiRequestOptions): Promise<T> {
      return request<T>('GET', path, null, options);
    },
    post<T = unknown>(path: string, data?: unknown, options?: ApiRequestOptions): Promise<T> {
      return request<T>('POST', path, data, options);
    },
    put<T = unknown>(path: string, data?: unknown, options?: ApiRequestOptions): Promise<T> {
      return request<T>('PUT', path, data, options);
    },
    delete<T = unknown>(path: string, options?: ApiRequestOptions): Promise<T> {
      return request<T>('DELETE', path, null, options);
    },
    async upload<T = unknown>(
      path: string,
      formData: FormData,
      options?: ApiRequestOptions,
    ): Promise<T> {
      const headers: Record<string, string> = {
        ...getAuthHeaders(),
      };
      for (const [key, value] of Object.entries(options?.headers ?? {})) {
        if (key.toLowerCase() === 'authorization') continue;
        headers[key] = value;
      }
      const response = await fetchWithRetry(
        `${baseUrl}${path}`,
        { method: 'POST', headers, body: formData, signal: options?.signal },
        { timeoutMs: 60000, timeoutLabel: 'Upload' },
        refreshAuthToken,
      );

      if (!response.ok) {
        throw new Error(await parseErrorMessage(response));
      }

      const json = await parseJsonEnvelope(response);
      return getEnvelopeData<T>(json);
    },
    async getHtml(path: string): Promise<string> {
      const headers = getAuthHeaders();
      const response = await fetchWithRetry(
        `${baseUrl}${path}`,
        { method: 'GET', headers },
        {},
        refreshAuthToken,
      );

      if (!response.ok) {
        throw new Error(await parseErrorMessage(response));
      }

      return response.text();
    },
    async postHtml(path: string, data: unknown): Promise<string> {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      };

      const response = await fetchWithRetry(
        `${baseUrl}${path}`,
        { method: 'POST', headers, body: JSON.stringify(data) },
        {},
        refreshAuthToken,
      );

      if (!response.ok) {
        throw new Error(await parseErrorMessage(response));
      }

      return response.text();
    },
    async getBlob(path: string, opts: FetchRetryOptions = {}): Promise<Blob> {
      const headers = getAuthHeaders();
      const response = await fetchWithRetry(
        `${baseUrl}${path}`,
        { method: 'GET', headers },
        opts,
        refreshAuthToken,
      );
      if (!response.ok) {
        throw new Error(await parseErrorMessage(response));
      }
      return response.blob();
    },
  };
}

